(() => {
  const SPEED_PX = 42;
  const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)");
  const RESIZE_DELAY = 250;

  const debounce = (fn, wait) => {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  };

  const stripEditorHooks = (element) => {
    element.removeAttribute("id");
    element.querySelectorAll("[data-shopify-editor-block], [id]").forEach((node) => {
      node.removeAttribute("data-shopify-editor-block");
      node.removeAttribute("id");
    });
  };

  const setup = (root) => {
    if (root.dataset.hearItBound) return;
    root.dataset.hearItBound = "true";

    const viewport = root.querySelector("[data-hear-it-viewport]");
    const track = root.querySelector("[data-hear-it-track]");
    const content = track && track.querySelector("[data-hear-it-content]");
    const group = content && content.querySelector("[data-hear-it-group]");
    if (!viewport || !track || !content || !group) return;

    let lastWidth = null;

    const motion = () => track.getAnimations()[0] || null;

    const teardown = () => {
      while (track.lastElementChild && track.lastElementChild !== content) {
        track.lastElementChild.remove();
      }
      while (content.lastElementChild && content.lastElementChild !== group) {
        content.lastElementChild.remove();
      }
      root.removeAttribute("data-hear-it-ready");
      root.removeAttribute("data-hear-it-measuring");
      root.style.removeProperty("--hear-it-speed");
    };

    const build = () => {
      teardown();

      /* Hold the animation still so a running transform cannot inflate widths. */
      root.setAttribute("data-hear-it-measuring", "");
      root.setAttribute("data-hear-it-ready", "");

      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const groupWidth = group.getBoundingClientRect().width;
      const viewWidth = viewport.getBoundingClientRect().width;
      if (!groupWidth || !viewWidth) {
        root.removeAttribute("data-hear-it-measuring");
        teardown();
        return;
      }
      lastWidth = viewWidth;

      /* One viewport of cards, then a mirror of that run. The CSS loop is
         translate3d(-50% - gap/2), so the two track children must stay equal. */
      const copies = Math.max(1, Math.ceil(viewWidth / groupWidth));
      for (let i = 1; i < copies; i += 1) {
        const extra = group.cloneNode(true);
        extra.setAttribute("aria-hidden", "true");
        extra.removeAttribute("data-hear-it-group");
        stripEditorHooks(extra);
        content.appendChild(extra);
      }

      const mirror = content.cloneNode(true);
      mirror.setAttribute("aria-hidden", "true");
      mirror.removeAttribute("data-hear-it-content");
      mirror.querySelectorAll("[data-hear-it-group]").forEach((node) => {
        node.removeAttribute("data-hear-it-group");
      });
      stripEditorHooks(mirror);
      track.appendChild(mirror);

      const distance = content.getBoundingClientRect().width + gap;
      root.style.setProperty("--hear-it-speed", `${Math.max(16, distance / SPEED_PX)}s`);
      root.removeAttribute("data-hear-it-measuring");

      requestAnimationFrame(() => {
        const animation = motion();
        if (animation) animation.currentTime = 0;
      });
    };

    const onResize = debounce(() => {
      if (REDUCED.matches) return;
      if (viewport.getBoundingClientRect().width === lastWidth) return;
      build();
    }, RESIZE_DELAY);

    window.addEventListener("resize", onResize, { passive: true });
    REDUCED.addEventListener("change", () => {
      if (REDUCED.matches) teardown();
      else build();
    });

    if (!REDUCED.matches) {
      if ("IntersectionObserver" in window) {
        const vis = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!root.hasAttribute("data-hear-it-ready")) build();
              const animation = motion();
              if (animation && !root.hasAttribute("data-hear-it-frozen")) animation.play();
            } else {
              const animation = motion();
              if (animation) animation.pause();
            }
          });
        }, { rootMargin: "160px" });
        vis.observe(viewport);
      } else {
        build();
      }
    }

    if (document.fonts && document.fonts.status !== "loaded") {
      document.fonts.ready.then(() => {
        if (!REDUCED.matches && root.hasAttribute("data-hear-it-ready")) build();
      });
    }

    const freeze = () => {
      root.setAttribute("data-hear-it-frozen", "");
      const animation = motion();
      if (animation) animation.pause();
    };

    const thaw = () => {
      if (!root.hasAttribute("data-hear-it-frozen")) return;
      root.removeAttribute("data-hear-it-frozen");
      const animation = motion();
      if (animation) animation.play();
    };

    let pressLink = null;

    root.addEventListener(
      "pointerdown",
      (event) => {
        if (event.button && event.button !== 0) return;
        const link = event.target.closest("a[href*='/products/']");
        if (!link) return;
        pressLink = link;
        freeze();
      },
      true
    );

    root.addEventListener(
      "pointerup",
      (event) => {
        const kept = pressLink && (pressLink === event.target || pressLink.contains(event.target));
        pressLink = null;
        if (!kept) thaw();
      },
      true
    );

    root.addEventListener("pointercancel", () => {
      pressLink = null;
      thaw();
    }, true);

    root.addEventListener(
      "click",
      (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          thaw();
          return;
        }
        if (!event.target.closest("a[href*='/products/']")) return;
        freeze();
      },
      true
    );

    window.addEventListener("pageshow", () => {
      pressLink = null;
      thaw();
    });
  };

  const init = (root) => {
    (root || document).querySelectorAll("[data-hear-it]").forEach(setup);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
